import { useState } from 'react'
import { putNode, deleteNode } from '../firebase/rest.js'

/**
 * Renders the dancer-facing request list for a single dance-type tab.
 * Items can be in one of three states:
 *   • pending  — votable, normal styling
 *   • queued   — instructor has placed it in their Spotify queue. Stays in
 *                the list (sorted alphabetically), dimmed, "· queued" tag,
 *                no vote button.
 *   • played   — Spotify confirmed it actually played. Same locked
 *                treatment as queued, but with a green "· played" tag.
 *
 * Voting is unlimited — a dancer can heart any song, just not more than
 * once each. No total-votes cap.
 *
 * Privacy: vote counts are NEVER shown to dancers. The dancer only sees
 * whether THEY personally voted (filled vs hollow heart on the toggle).
 * The list is also sorted alphabetically here, not by vote count, so the
 * ranking can't leak via positional ordering either. The instructor view
 * has full vote-count visibility and ranks pending requests by votes.
 */
export default function RequestList({ requests, deviceId }) {
  return (
    <ul className="space-y-2">
      {requests.map((r, idx) => (
        <RequestItem
          key={r.id}
          request={r}
          deviceId={deviceId}
          rank={idx + 1}
        />
      ))}
    </ul>
  )
}

function RequestItem({ request, deviceId, rank }) {
  const isQueued = request.status === 'queued'
  const isPlayed = request.status === 'played'
  const isLocked = isQueued || isPlayed
  const myVote = Boolean(request.votes && request.votes[deviceId])
  const [pending, setPending] = useState(false)

  // Retract eligibility:
  //   • dancer must be the original submitter (matching device_id)
  //   • request must still be pending (queued/played are past the point)
  //   • vote count must be ≤ 1 — if other dancers have voted, retracting
  //     would erase their interest. Block retract entirely in that case.
  // The dancer's own auto-vote on submission counts as 1, so voteCount === 1
  // means only-me. voteCount > 1 means at least one other dancer voted.
  const isMine = request.device_id === deviceId
  const canRetract = isMine && !isLocked && (request.voteCount ?? 0) <= 1

  async function toggleVote() {
    if (isLocked) return
    if (pending) return
    setPending(true)
    try {
      const path = `requests/${request.id}/votes/${deviceId}`
      if (myVote) {
        await putNode(path, null)
      } else {
        await putNode(path, true)
      }
    } finally {
      setPending(false)
    }
  }

  async function retract() {
    if (!canRetract || pending) return
    if (!confirm('Retract this request? Your quota slot will be returned.')) return
    setPending(true)
    try {
      await deleteNode(`requests/${request.id}`)
    } finally {
      setPending(false)
    }
  }

  return (
    <li className={`card flex items-center gap-3 ${isLocked ? 'opacity-75' : ''}`}>
      <div className="w-6 text-center flex-none">
        <span className="text-peak-muted text-sm">#{rank}</span>
      </div>
      {request.album_art ? (
        <img
          src={request.album_art}
          alt=""
          className="w-10 h-10 rounded object-cover flex-none"
        />
      ) : (
        <div className="w-10 h-10 rounded bg-white/5 grid place-items-center flex-none text-peak-muted text-sm">
          ♪
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{request.song_title}</p>
        <p className="text-xs text-peak-muted truncate">
          {request.artist || 'Artist not specified'} · <span className="capitalize">{request.dance_type}</span>
          {isQueued && <span className="ml-1 text-peak-warn">· queued</span>}
          {isPlayed && <span className="ml-1 text-peak-ok">· played</span>}
        </p>
      </div>
      {canRetract && (
        <button
          onClick={retract}
          disabled={pending}
          className="text-peak-muted hover:text-red-300 text-xs px-2 py-1 rounded-lg flex-none"
          aria-label="Retract this request"
          title="Retract — return this request slot to your quota"
        >
          Retract
        </button>
      )}
      {/* Heart toggle — shows only THIS dancer's vote state, no count.
          Filled heart = you voted, hollow heart = you haven't. Locked
          rows (queued/played) show a static "you voted" tag if they did,
          otherwise nothing — no count for either case. Vote counts are
          instructor-only. */}
      {isLocked ? (
        myVote && (
          <span className="px-3 py-2 rounded-xl text-sm bg-white/5 text-peak-muted" title="You voted for this">
            ♥
          </span>
        )
      ) : (
        <button
          onClick={toggleVote}
          disabled={pending}
          className={`px-3 py-2 rounded-xl text-lg leading-none transition-colors ${
            myVote
              ? 'bg-peak-accent text-white'
              : 'bg-white/5 text-white hover:bg-white/10'
          }`}
          aria-label={myVote ? 'Remove vote' : 'Add vote'}
          title={myVote ? 'Remove your vote' : 'Add your vote'}
        >
          {myVote ? '♥' : '♡'}
        </button>
      )}
    </li>
  )
}
